import React from 'react';
import { T, sx } from '../lib/tokens';
import { useIsMobile } from '../lib/useIsMobile';
import { Wordmark } from '../components/ui/Wordmark';
import { Button } from '../components/ui/Button';
import { Arrow, Check, XeroX, Email, Upload, Camera, SMS, Scan, Doc, Loop, Attach, Sparkle } from '../components/ui/Icons';

interface Props {
  onSignUp: () => void;
  onSignIn: () => void;
}

export function Landing({ onSignUp, onSignIn }: Props) {
  const m = useIsMobile();
  const pad = m ? '0 20px' : '0 40px';
  const ctr: React.CSSProperties = { maxWidth: 1240, margin: '0 auto', padding: pad };

  return (
    <div style={{ background: T.navy, color: T.cream, minHeight: '100vh' }}>
      {/* Nav */}
      <header style={{ position: 'sticky', top: 0, zIndex: 10, background: 'rgba(10,20,40,0.82)', backdropFilter: 'blur(14px)', borderBottom: `1px solid ${T.navyLine}` }}>
        <div style={{ ...ctr, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: m ? '14px 20px' : '20px 40px' }}>
          <Wordmark size={m ? 20 : 26} />
          {!m && (
            <nav style={{ display: 'flex', gap: 32, fontSize: 14, color: T.creamMuted }}>
              <a href="#how" style={{ color: 'inherit', textDecoration: 'none' }}>How it works</a>
              <a href="#accuracy" style={{ color: 'inherit', textDecoration: 'none' }}>Accuracy</a>
              <a href="#pricing" style={{ color: 'inherit', textDecoration: 'none' }}>Pricing</a>
            </nav>
          )}
          <div style={{ display: 'flex', gap: m ? 12 : 14, alignItems: 'center' }}>
            <span onClick={onSignIn} style={{ fontSize: m ? 13 : 14, color: T.creamMuted, cursor: 'pointer' }}>Sign in</span>
            <Button size={m ? 'small' : 'default'} onClick={onSignUp}>{m ? 'Sign up' : 'Sign up free'}</Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section style={{ padding: m ? '60px 0 70px' : '110px 0 130px', overflow: 'hidden' }}>
        <div style={ctr}>
          <div style={m ? {} : { display: 'grid', gridTemplateColumns: '1.45fr 1fr', gap: 80, alignItems: 'end' }}>
            <div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '8px 16px 8px 10px', border: '1px solid rgba(19,181,234,0.35)', background: 'rgba(19,181,234,0.08)', borderRadius: 100, marginBottom: m ? 24 : 36 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <XeroX />
                  <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#6B2C91', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12 }}>M</div>
                  <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#2CA01C', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12 }}>Q</div>
                </div>
                <span style={{ fontSize: 13, color: '#7ED5F5', fontWeight: 500 }}>Built for Xero, MYOB & QuickBooks</span>
              </div>
              <h1 style={{ ...sx.serif, fontSize: m ? 'clamp(40px, 12vw, 64px)' : 'clamp(68px, 8.6vw, 124px)', lineHeight: 0.92, letterSpacing: '-0.04em', margin: m ? '0 0 20px' : '0 0 32px', fontWeight: 400 }}>
                Stop doing<br />your <em style={{ fontStyle: 'italic', color: T.sage }}>bloody</em><br />receipts.
              </h1>
              <p style={{ fontSize: m ? 16 : 21, lineHeight: 1.5, color: T.creamMuted, margin: m ? '0 0 28px' : '0 0 40px', maxWidth: 620 }}>
                Forward an email, upload a PDF, or snap a photo. Our AI reads the receipt, extracts every field, picks the right account, and creates a Spend Money / Purchase transaction in your books with the receipt attached. You don't lift a finger.
              </p>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button size={m ? 'default' : 'large'} onClick={onSignUp} style={m ? { width: '100%' } : {}}>
                  Sign up free <Arrow color={T.navy} />
                </Button>
                <span style={{ fontSize: 13, color: T.creamDim }}>{m ? 'First 20 free' : 'First 20 receipts free · no credit card required'}</span>
              </div>
            </div>
            {/* Stats */}
            <div style={m ? { display: 'flex', gap: 20, marginTop: 40, borderTop: `1px solid ${T.navyLine}`, paddingTop: 24 } : { display: 'grid', gap: 28, alignSelf: 'end' }}>
              {[
                { num: '95', small: '%', label: 'AI accuracy on first pass' },
                { num: '12', small: 's', label: 'email to posted transaction' },
                { num: '0', small: ' clicks', label: 'to reconcile most receipts' },
              ].map((s, i) => (
                <div key={i} style={m ? { flex: 1, textAlign: 'center' } : { borderTop: `1px solid ${T.navyLine}`, paddingTop: 20 }}>
                  <div style={{ ...sx.serif, fontSize: m ? 36 : 64, lineHeight: 1, letterSpacing: '-0.03em' }}>
                    {s.num}<small style={{ fontSize: m ? 14 : 26, color: T.creamFaint }}>{s.small}</small>
                  </div>
                  <div style={{ fontSize: m ? 10 : 12, color: T.creamDim, marginTop: m ? 6 : 10, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Channels */}
      <section id="how" style={{ padding: m ? '60px 0' : '100px 0', borderTop: `1px solid ${T.navyLine}` }}>
        <div style={ctr}>
          <div style={{ ...sx.label, marginBottom: 20 }}>Get receipts in however you like</div>
          <h2 style={{ ...sx.serif, fontSize: m ? 32 : 64, lineHeight: 1, letterSpacing: '-0.03em', fontWeight: 400, marginBottom: m ? 32 : 56, maxWidth: 860 }}>
            Five ways in. <em style={{ color: T.sage, fontStyle: 'italic' }}>One way out.</em>
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: m ? '1fr' : 'repeat(5, 1fr)', gap: m ? 12 : 16 }}>
            {[
              { icon: Email, title: 'Email forward', text: 'Forward any receipt email to your unique receiptrec address.', soon: false },
              { icon: Upload, title: 'Drag and drop', text: 'Drop a PDF or image right onto the upload zone.', soon: false },
              { icon: Camera, title: 'Snap a photo', text: 'Open the app on your phone, point at the receipt, done.', soon: false },
              { icon: Scan, title: 'Gmail auto-sync', text: 'Connect once. Every hour we sweep your inbox and pull out new receipts automatically.', soon: false, featured: true },
              { icon: SMS, title: 'SMS forward', text: 'Forward receipts straight from your messages app.', soon: true, featured: false },
            ].map((c: { icon: any; title: string; text: string; soon: boolean; featured?: boolean }, i) => {
              const IC = c.icon;
              return (
                <div key={i} style={{ background: c.featured ? 'rgba(168,199,158,0.08)' : T.navy2, border: c.featured ? `1px solid rgba(168,199,158,0.45)` : `1px solid ${T.navyLine}`, borderRadius: 14, padding: m ? '20px 18px' : '28px 24px 26px', display: 'flex', flexDirection: m ? 'row' : 'column', alignItems: m ? 'center' : 'flex-start', gap: m ? 14 : 16, opacity: c.soon ? 0.78 : 1, position: 'relative' }}>
                  <div style={{ width: 36, height: 36, background: c.soon ? 'rgba(245,240,230,0.08)' : 'rgba(168,199,158,0.18)', borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <IC />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: m ? 14 : 16, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: 0 }}>
                      {c.title}
                      {c.soon && <span style={{ padding: '3px 9px', borderRadius: 100, fontSize: 10, fontWeight: 500, background: 'rgba(168,199,158,0.12)', color: T.sage, border: '1px solid rgba(168,199,158,0.3)' }}>Soon</span>}
                      {c.featured && <span style={{ padding: '3px 9px', borderRadius: 100, fontSize: 10, fontWeight: 600, background: T.sage, color: T.navy, letterSpacing: '0.05em', textTransform: 'uppercase' }}>New</span>}
                    </h3>
                    <p style={{ fontSize: m ? 12 : 13, color: T.creamMuted, lineHeight: 1.5, marginTop: m ? 4 : 8 }}>{c.text}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Gmail auto-sync headline */}
          <div style={{ marginTop: m ? 32 : 56, background: 'linear-gradient(135deg, rgba(168,199,158,0.14), rgba(168,199,158,0.04))', border: `1px solid rgba(168,199,158,0.35)`, borderRadius: 18, padding: m ? '28px 22px' : '48px 56px', display: 'grid', gridTemplateColumns: m ? '1fr' : '1.2fr 1fr', gap: m ? 28 : 56, alignItems: 'center' }}>
            <div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 100, background: T.sage, color: T.navy, fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: m ? 18 : 24 }}>
                <Sparkle size={12} /> Just launched
              </div>
              <h3 style={{ ...sx.serif, fontSize: m ? 32 : 52, lineHeight: 1.02, letterSpacing: '-0.03em', fontWeight: 400, marginTop: 0, marginBottom: m ? 14 : 20 }}>
                Never forward a receipt <em style={{ color: T.sage, fontStyle: 'italic' }}>again.</em>
              </h3>
              <p style={{ fontSize: m ? 15 : 17, color: T.creamMuted, lineHeight: 1.55, margin: 0, marginBottom: m ? 20 : 28 }}>
                Connect Gmail once. Every hour we quietly scan for invoices, receipts and order confirmations, pull the attachments, run them through the AI, and post them to your books. You literally forget about it.
              </p>
              <div style={{ display: 'grid', gap: m ? 10 : 12, marginBottom: m ? 22 : 0 }}>
                {[
                  'Read-only Gmail access — revoke any time',
                  'Runs every hour in the background',
                  'Skips anything that isn\'t a receipt',
                  'Included on every plan — even Free',
                ].map((p, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: m ? 13 : 14, color: T.cream }}>
                    <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'rgba(168,199,158,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Check size={10} color={T.sage} />
                    </div>
                    {p}
                  </div>
                ))}
              </div>
            </div>
            <div style={{ background: T.navy, border: `1px solid ${T.navyLine}`, borderRadius: 14, padding: m ? 22 : 28, fontFamily: sx.mono.fontFamily as string }}>
              <div style={{ fontSize: 11, color: T.creamDim, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 14 }}>Inbox · last hour</div>
              {[
                { from: 'billing@aws.amazon.com', subj: 'Your March 2026 invoice', tag: 'Posted' },
                { from: 'receipts@uber.com', subj: 'Trip receipt — Fri 9:42pm', tag: 'Posted' },
                { from: 'noreply@adobe.com', subj: 'Creative Cloud — renewal', tag: 'Posted' },
                { from: 'accounts@mailchimp.com', subj: 'Receipt for payment', tag: 'Review' },
              ].map((e, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderTop: i === 0 ? 'none' : `1px solid ${T.navyLine}` }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, color: T.creamMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.from}</div>
                    <div style={{ fontSize: 13, color: T.cream, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 2 }}>{e.subj}</div>
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 100, background: e.tag === 'Posted' ? 'rgba(168,199,158,0.2)' : 'rgba(245,198,109,0.2)', color: e.tag === 'Posted' ? T.sage : '#F5C66D', letterSpacing: '0.05em', textTransform: 'uppercase', flexShrink: 0 }}>{e.tag}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AI Pipeline */}
      <section style={{ padding: m ? '60px 0' : '120px 0', background: T.paper, color: T.graphite }}>
        <div style={ctr}>
          <div style={{ ...sx.labelLight, marginBottom: 20 }}>What the AI actually does</div>
          <h2 style={{ ...sx.serif, fontSize: m ? 36 : 72, lineHeight: 0.98, letterSpacing: '-0.03em', fontWeight: 400, maxWidth: 880, marginBottom: m ? 12 : 20 }}>
            Not another <em style={{ color: T.sageDarker, fontStyle: 'italic' }}>OCR wrapper.</em>
          </h2>
          <p style={{ fontSize: m ? 15 : 18, color: T.graphiteMuted, maxWidth: 620, marginBottom: m ? 32 : 72, lineHeight: 1.55 }}>
            Every receipt goes through four AI steps before it touches your books.
          </p>
          <div style={{ background: '#fff', border: `1px solid ${T.paperLine}`, borderRadius: 18, padding: m ? 20 : 40, display: 'grid', gridTemplateColumns: m ? '1fr' : '1fr auto 1fr auto 1fr auto 1fr', gap: m ? 24 : 20, alignItems: m ? 'stretch' : 'center' }}>
            {[
              { n: 'Step 01', h: 'Extract everything', p: 'Vendor, date, total, tax, line items, currency, payment method.', badge: 'All fields captured' },
              { n: 'Step 02', h: 'Match the account', p: 'The AI picks the right category from your chart of accounts.', badge: 'Your chart of accounts' },
              { n: 'Step 03', h: 'Create Spend Money', p: 'Posts a transaction in your books with correct GST and account code.', badge: 'Native accounting transaction' },
              { n: 'Step 04', h: 'Attach the receipt', p: 'The original file gets attached as source evidence.', badge: 'Source evidence kept' },
            ].map((s, i) => (
              <React.Fragment key={i}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: m ? 8 : 14 }}>
                  <div style={{ ...sx.serif, fontSize: 13, color: T.sageDarker, letterSpacing: '0.12em', textTransform: 'uppercase' }}>{s.n}</div>
                  <h3 style={{ fontSize: m ? 16 : 18, fontWeight: 500, color: T.graphite, lineHeight: 1.25, margin: 0 }}>{s.h}</h3>
                  <p style={{ fontSize: 13, color: T.graphiteMuted, lineHeight: 1.5, margin: 0 }}>{s.p}</p>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 9px', borderRadius: 100, background: 'rgba(168,199,158,0.2)', color: T.sageDarker, fontSize: 11, fontWeight: 500, width: 'fit-content' }}>
                    <Check size={10} color={T.sageDarker} />{s.badge}
                  </span>
                </div>
                {!m && i < 3 && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M5 12H19M14 7L19 12L14 17" stroke={T.paperLine} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                )}
              </React.Fragment>
            ))}
          </div>
          <div style={{ marginTop: m ? 20 : 40, display: 'grid', gridTemplateColumns: m ? '1fr' : '1fr 1fr', gap: m ? 12 : 24 }}>
            <div style={{ background: '#fff', border: `1px solid ${T.paperLine}`, borderRadius: 14, padding: m ? '20px' : '28px 32px' }}>
              <h4 style={{ ...sx.serif, fontSize: m ? 24 : 32, letterSpacing: '-0.02em', fontWeight: 400, marginBottom: 8 }}>No <em style={{ color: T.sageDarker, fontStyle: 'italic' }}>data entry.</em></h4>
              <p style={{ fontSize: 14, lineHeight: 1.55, color: T.graphiteMuted }}>You don't type a single field. The AI does the whole job.</p>
            </div>
            <div style={{ background: '#fff', border: `1px solid ${T.paperLine}`, borderRadius: 14, padding: m ? '20px' : '28px 32px' }}>
              <h4 style={{ ...sx.serif, fontSize: m ? 24 : 32, letterSpacing: '-0.02em', fontWeight: 400, marginBottom: 8 }}>No <em style={{ color: T.sageDarker, fontStyle: 'italic' }}>silent mistakes.</em></h4>
              <p style={{ fontSize: 14, lineHeight: 1.55, color: T.graphiteMuted }}>If the AI isn't confident, the receipt waits for your review.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Accuracy */}
      <section id="accuracy" style={{ padding: m ? '60px 0' : '140px 0', borderTop: `1px solid ${T.navyLine}` }}>
        <div style={ctr}>
          <div style={m ? {} : { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'center' }}>
            <div>
              <div style={{ ...sx.label, marginBottom: 20 }}>Why we're different</div>
              <h2 style={{ ...sx.serif, fontSize: m ? 36 : 64, lineHeight: 1, letterSpacing: '-0.03em', fontWeight: 400, marginBottom: 24 }}>
                95% accurate.<br />And <em style={{ color: T.sage, fontStyle: 'italic' }}>getting smarter</em>.
              </h2>
              <p style={{ fontSize: m ? 15 : 17, color: T.creamMuted, lineHeight: 1.55, marginBottom: 20 }}>
                We use frontier AI models to actually understand what a receipt means.
              </p>
              <div style={{ display: 'grid', gap: 18, marginTop: 32 }}>
                {[
                  { b: 'Context, not characters.', t: 'Our AI reads "AWS - March 2026 usage" and understands it\'s cloud infrastructure.' },
                  { b: 'Learns from your edits.', t: 'Correct us once and we\'ll remember.' },
                  { b: 'Knows when it doesn\'t know.', t: 'Low-confidence receipts get held for review.' },
                  { b: 'Handles the ugly stuff.', t: 'Blurry photos, folded paper, email threads with six forwards.' },
                ].map((p, i) => (
                  <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(168,199,158,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                      <Check size={12} color={T.sage} />
                    </div>
                    <div style={{ fontSize: m ? 14 : 15, color: T.cream, lineHeight: 1.5 }}>
                      <strong style={{ fontWeight: 500 }}>{p.b}</strong> {p.t}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ background: T.navy2, border: `1px solid ${T.navyLine}`, borderRadius: 18, padding: m ? '28px 24px' : '44px 40px', marginTop: m ? 32 : 0 }}>
              <div style={{ ...sx.serif, fontSize: m ? 100 : 180, lineHeight: 0.88, letterSpacing: '-0.045em', color: T.sage, fontWeight: 400 }}>
                95<small style={{ fontSize: m ? 36 : 60, color: 'rgba(168,199,158,0.55)' }}>%</small>
              </div>
              <div style={{ fontSize: 13, color: T.creamMuted, marginTop: 18, lineHeight: 1.5 }}>
                of receipts posted to your books correctly on the first try.
              </div>
              <div style={{ marginTop: 24, paddingTop: 20, borderTop: `1px solid ${T.navyLine}`, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(168,199,158,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Loop />
                </div>
                <div style={{ fontSize: 13, color: T.creamMuted, lineHeight: 1.55 }}>
                  <strong style={{ color: T.cream, fontWeight: 500 }}>Human in the loop.</strong> Corrections feed back into how we match your future receipts.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Xero */}
      <section style={{ padding: m ? '60px 0' : '120px 0', background: T.paper, color: T.graphite }}>
        <div style={ctr}>
          <div style={m ? {} : { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'center' }}>
            <div>
              <div style={{ ...sx.labelLight, marginBottom: 20 }}>Fits how you already work.</div>
              <h2 style={{ ...sx.serif, fontSize: m ? 32 : 64, lineHeight: 1, letterSpacing: '-0.03em', fontWeight: 400, marginBottom: 24 }}>
                Because you<br />already use <em style={{ color: T.sageDarker, fontStyle: 'italic' }}>your books.</em>
              </h2>
              <p style={{ fontSize: m ? 15 : 17, color: T.graphiteMuted, lineHeight: 1.55, marginBottom: 20 }}>
                Every field, every tax rate, every tracking category is designed around how Xero, MYOB, and QuickBooks actually work.
              </p>
              <div style={{ display: 'grid', gap: 14, marginTop: 32 }}>
                {[
                  { b: 'Native Spend Money transactions.', t: 'Proper Spend Money posts against your default bank account.' },
                  { b: 'Your full chart of accounts.', t: 'All expense codes, all your custom accounts.' },
                  { b: 'GST handled correctly.', t: 'BAS excluded, GST on expenses, input-taxed.' },
                  { b: 'Source receipt attached.', t: 'Every transaction gets the original receipt attached.' },
                ].map((p, i) => (
                  <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', fontSize: m ? 14 : 15, color: T.graphite, lineHeight: 1.5 }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: T.sageDarker, marginTop: 9, flexShrink: 0 }} />
                    <div><strong style={{ fontWeight: 500 }}>{p.b}</strong> {p.t}</div>
                  </div>
                ))}
              </div>
            </div>
            {/* Xero mock card */}
            <div style={{ background: '#fff', border: `1px solid ${T.paperLine}`, borderRadius: 16, padding: m ? 20 : 32, marginTop: m ? 32 : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, paddingBottom: 14, borderBottom: '1px solid #EDE7D8' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: T.graphiteDim }}>
                  <XeroX />
                  <span>Xero - Spend Money</span>
                </div>
                <div style={{ fontSize: 11, color: T.graphiteDim, ...sx.mono }}>#SM-00241</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px dashed #EDE7D8' }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(19,181,234,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Doc />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: T.graphite }}>Amazon Web Services</div>
                  <div style={{ fontSize: 12, color: T.graphiteDim, marginTop: 2, ...sx.mono }}>485 - Cloud infrastructure</div>
                </div>
                <div style={{ ...sx.mono, fontSize: 14, fontWeight: 500, color: T.graphite, flexShrink: 0 }}>AUD 412.87</div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px dashed #EDE7D8', marginLeft: 44 }}>
                <div style={{ fontSize: 12, color: T.graphiteDim }}>GST (BAS excluded)</div>
                <div style={{ ...sx.mono, fontSize: 14, fontWeight: 500, color: T.graphiteDim }}>37.53</div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', marginLeft: 44 }}>
                <div style={{ fontSize: 12, color: T.graphiteDim }}>Paid from</div>
                <div style={{ ...sx.mono, fontSize: 12, color: T.graphite }}>Business One - 6411</div>
              </div>
              <div style={{ marginTop: 14, padding: '10px 12px', background: 'rgba(168,199,158,0.1)', border: '1px solid rgba(90,142,76,0.2)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: T.sageDarker }}>
                <div style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(168,199,158,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Attach />
                </div>
                <div style={{ lineHeight: 1.4, minWidth: 0 }}>
                  <strong style={{ fontWeight: 500, color: T.graphite }}>AWS_invoice_mar2026.pdf</strong> attached
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote */}
      <section style={{ padding: m ? '60px 0' : '130px 0', textAlign: 'center', borderTop: `1px solid ${T.navyLine}` }}>
        <div style={ctr}>
          <blockquote style={{ ...sx.serif, fontSize: m ? 28 : 52, lineHeight: 1.12, letterSpacing: '-0.025em', maxWidth: 960, margin: '0 auto' }}>
            "I used to spend every Sunday night hating my life and entering receipts into my books. <em style={{ color: T.sage, fontStyle: 'italic' }}>Now I don't.</em> That's the whole pitch."
          </blockquote>
          <div style={{ marginTop: m ? 24 : 40, fontSize: m ? 12 : 14, color: T.creamMuted, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: T.sage, color: T.navy, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600 }}>SR</div>
            <div>Sam Rafferty - Plumber, Brunswick VIC</div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" style={{ padding: m ? '60px 0' : '120px 0', borderTop: `1px solid ${T.navyLine}` }}>
        <div style={ctr}>
          <div style={{ ...sx.label, marginBottom: 20 }}>Pricing</div>
          <h2 style={{ ...sx.serif, fontSize: m ? 36 : 64, lineHeight: 1, letterSpacing: '-0.03em', fontWeight: 400, marginBottom: m ? 8 : 16 }}>
            Start free. <em style={{ color: T.sage, fontStyle: 'italic' }}>Pay when it matters.</em>
          </h2>
          <p style={{ fontSize: m ? 15 : 17, color: T.creamMuted, maxWidth: 620, marginBottom: m ? 32 : 56, lineHeight: 1.55 }}>
            20 receipts free to get you started. After that, pick the plan that matches your volume. Cancel any time.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: m ? '1fr' : 'repeat(4, 1fr)', gap: m ? 14 : 16, alignItems: 'stretch' }}>
            {(() => {
              const plans = [
              {
                name: 'Free',
                price: '$0',
                period: '',
                tag: '',
                highlight: false,
                features: ['20 receipts lifetime', 'Full AI + accounting posting', 'Upload, email forward, photo', 'Gmail auto-sync included'],
                cta: 'Get started',
                ctaAction: onSignUp,
              },
              {
                name: 'Solo',
                price: '$9.99',
                period: '/mo',
                tag: '',
                highlight: false,
                features: ['75 receipts / month', 'Everything in Free', 'Gmail auto-sync', 'Category rules that learn', 'Email support', '$0.15 per extra receipt'],
                cta: 'Start with Solo',
                ctaAction: onSignUp,
              },
              {
                name: 'Business',
                price: '$19.99',
                period: '/mo',
                tag: 'Most popular',
                highlight: true,
                features: ['300 receipts / month', 'Everything in Solo', 'Priority email support', 'Multiple sender email aliases', 'Bulk receipt actions', '$0.15 per extra receipt'],
                cta: 'Start with Business',
                ctaAction: onSignUp,
              },
              {
                name: 'Pro',
                price: '$39.99',
                period: '/mo',
                tag: '',
                highlight: false,
                features: ['1000 receipts / month', 'Everything in Business', 'Multiple company files', 'Outlook sync (soon)', 'Priority chat support', '$0.10 per extra receipt'],
                cta: 'Start with Pro',
                ctaAction: onSignUp,
              },
              ];
              // On mobile, surface Business first (recommended), then Solo, Pro, Free.
              // Desktop keeps the Free → Pro progression.
              const ordered = m
                ? ['Business', 'Solo', 'Pro', 'Free'].map((n) => plans.find((p) => p.name === n)!)
                : plans;
              return ordered.map((plan, i) => (
              <div
                key={plan.name}
                style={{
                  background: T.navy2,
                  border: `1px solid ${plan.highlight ? T.sage : T.navyLine}`,
                  borderRadius: 18,
                  padding: m ? '24px 20px' : '28px 24px',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14,
                }}
              >
                {plan.tag && (
                  <div style={{ position: 'absolute', top: -10, left: m ? 16 : 20, padding: '4px 10px', borderRadius: 100, background: T.sage, color: T.navy, fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    {plan.tag}
                  </div>
                )}
                <div style={{ fontSize: 13, color: T.sage, fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{plan.name}</div>
                <div style={{ ...sx.serif, fontSize: m ? 42 : 48, lineHeight: 1, letterSpacing: '-0.03em' }}>
                  {plan.price}<small style={{ fontSize: 16, color: T.creamFaint, fontFamily: 'Geist, sans-serif', fontWeight: 400 }}>{plan.period}</small>
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 8, flex: 1 }}>
                  {plan.features.map((f, j) => (
                    <li key={j} style={{ fontSize: 13, color: T.cream, paddingLeft: 20, position: 'relative', lineHeight: 1.45 }}>
                      <span style={{ position: 'absolute', left: 0, top: 6, width: 10, height: 5, borderLeft: `1.5px solid ${T.sage}`, borderBottom: `1.5px solid ${T.sage}`, transform: 'rotate(-45deg)' }} />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button onClick={plan.ctaAction} style={{ width: '100%', padding: '11px 16px', fontSize: 13 }}>
                  {plan.cta} <Arrow color={T.navy} size={14} />
                </Button>
              </div>
            ));
            })()}
          </div>
          <div style={{ marginTop: m ? 24 : 32, padding: '14px 18px', background: 'rgba(168,199,158,0.08)', border: `1px solid ${T.navyLine}`, borderRadius: 12, display: 'flex', alignItems: 'center', gap: 10, maxWidth: 520 }}>
            <span style={{ fontSize: 18 }}>🎟️</span>
            <span style={{ fontSize: 13, color: T.creamMuted, lineHeight: 1.5 }}>
              Got a promo code? You&apos;ll be able to apply it at checkout.
            </span>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={{ padding: m ? '60px 0' : '140px 0', textAlign: 'center', borderTop: `1px solid ${T.navyLine}` }}>
        <div style={ctr}>
          <h2 style={{ ...sx.serif, fontSize: m ? 36 : 92, lineHeight: 0.95, letterSpacing: '-0.035em', fontWeight: 400, marginBottom: m ? 20 : 36 }}>
            Your Sunday nights<br /><em style={{ color: T.sage, fontStyle: 'italic' }}>back, starting today.</em>
          </h2>
          <p style={{ fontSize: m ? 15 : 18, color: T.creamMuted, maxWidth: 540, margin: '0 auto', marginBottom: m ? 28 : 40, lineHeight: 1.55 }}>Pick your platform and get started. First 20 receipts are free.</p>
          <Button size={m ? 'default' : 'large'} onClick={onSignUp} style={m ? { width: '100%' } : {}}>Sign up free <Arrow color={T.navy} /></Button>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ padding: m ? '40px 0 30px' : '60px 0 40px', borderTop: `1px solid ${T.navyLine}` }}>
        <div style={ctr}>
          <div style={m ? { display: 'grid', gap: 32 } : { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 40 }}>
            <div>
              <Wordmark size={22} />
              <div style={{ fontSize: 13, color: T.creamDim, marginTop: 10, maxWidth: 280, lineHeight: 1.5 }}>
                Receipt in. Reconciled.<br />Built for Xero, MYOB & QuickBooks. Made in Melbourne.
              </div>
            </div>
            <div style={m ? { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 } : { display: 'flex', gap: 60 }}>
              {[
                { head: 'Product', items: [
                  { label: 'How it works', href: '#how' },
                  { label: 'Accuracy', href: '#accuracy' },
                  { label: 'Pricing', href: '#pricing' },
                ] },
                { head: 'Company', items: [
                  { label: 'About', href: '#/about' },
                  { label: 'Contact', href: 'mailto:hello@receiptrec.com' },
                ] },
                { head: 'Legal', items: [
                  { label: 'Privacy', href: '#/privacy' },
                  { label: 'Terms', href: '#/terms' },
                  { label: 'Security', href: '#/security' },
                ] },
              ].map((col, i) => (
                <div key={i} style={{ display: 'grid', gap: 8 }}>
                  <div style={{ ...sx.label, marginBottom: 4 }}>{col.head}</div>
                  {col.items.map((it) => (
                    <a
                      key={it.label}
                      href={it.href}
                      style={{ fontSize: m ? 13 : 14, color: T.creamMuted, textDecoration: 'none' }}
                    >
                      {it.label}
                    </a>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div style={{ marginTop: m ? 24 : 40, paddingTop: 20, borderTop: `1px solid ${T.navyLine}`, display: 'flex', justifyContent: 'space-between', fontSize: 12, color: T.creamDim, flexWrap: 'wrap', gap: 12 }}>
            <span>&copy; 2026 receiptrec &middot; Crewamp Pty Ltd</span>
            <span>receiptrec.com</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
